/**
 * Responsibility: decide where standing rock columns — spires and hoodoos — go.
 *
 * ## Why this is shared rather than living in a biome
 *
 * Mesa country and canyon country are the same geology, and columns belong in both. One generator called from each
 * biome's `createStructures` keeps a single authoritative placement rule instead of two copies that drift apart.
 *
 * ## Why columns are structures and not terrain
 *
 * A heightfield cannot represent a column. Terrain is sampled on a lattice about 48 units apart, so the narrowest
 * thing it can express is roughly one cell wide, and a spire is narrower. Worse, a hoodoo's caprock is *wider than
 * the shaft beneath it* — an overhang, which a height field cannot represent at any resolution, because it has one
 * height per coordinate by definition.
 *
 * As structures they inherit slot placement, slope rejection, depth masks and cross-biome clearance for free.
 *
 * ## Fields, not a sprinkle
 *
 * Columns come in groups: Bryce is a dense forest of hoodoos, Monument Valley is isolated towers in empty desert. A
 * medium-frequency group field gives both, exactly as the forest's stand field does — and for the same reason, since
 * an even sprinkle is the failure mode in both cases.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createFbm, toUnitRange } from './noise.js';
import { createLocalRng, deriveSeed } from './rng.js';
import { widestRadiusFor } from './structureGeometry.js';
import { ROCK_COLUMNS } from '../config.js';

/** Linear interpolation, for turning a 0..1 roll into a range. */
function lerp(from, to, t) {
  return from + (to - from) * t;
}

function clamp01(value) {
  return Math.min(1, Math.max(0, value));
}

/**
 * Range of slot indices whose centres fall inside a span.
 *
 * One tile owns each slot, so a column near a tile edge is emitted exactly once. Getting this wrong duplicates a row
 * of columns along every seam or leaves a gap — the mistake farmland made with its fields.
 */
function slotRange(start, length, spacing) {
  return {
    first: Math.ceil(start / spacing - 0.5),
    last: Math.ceil((start + length) / spacing - 0.5) - 1,
  };
}

/**
 * Build a rock column placement function for a biome.
 *
 * @param {number} seed - Base world seed.
 * @param {string} biomeId - Used to derive an independent stream, so mesa and canyon country do not place columns in
 *   identical patterns where they happen to sit at the same coordinates.
 * @returns {(region: object, context: object) => Array<object>}
 */
export function createRockColumns(seed, biomeId) {
  /*
    A dedicated seed per biome, so column placement is independent of the biome's own suitability field, of its ground,
    and of the other rock biome's columns.
  */
  const columnSeed = deriveSeed(seed, `columns:${biomeId}`);

  const groupField = createFbm(seed, `columngroups:${biomeId}`, {
    octaves: ROCK_COLUMNS.GROUP_OCTAVES,
    baseFrequency: 1 / ROCK_COLUMNS.GROUP_WAVELENGTH,
  });

  /** Widest a base may be, whatever the height suggests, so two neighbours can never touch. */
  const maxRadius = ROCK_COLUMNS.SPACING * ROCK_COLUMNS.MAX_RADIUS_FRACTION;

  /**
   * How strongly a group covers this point, 0 to 1.
   *
   * Zero below the threshold, which is what makes the desert between fields genuinely empty rather than merely
   * thinner — the difference between fields of hoodoos and an even scatter.
   */
  function groupStrength(x, z) {
    const raw = toUnitRange(groupField(x, z));
    if (raw <= ROCK_COLUMNS.GROUP_THRESHOLD) return 0;

    /*
      Ramped over a narrow band rather than the whole remaining range. **fBm never approaches its extremes**, so
      dividing by `1 - threshold` leaves strength near zero everywhere and produces a thin scatter. The forest hit
      this exact wall with its stand field, and so did the mesa's terracing.
    */
    return clamp01((raw - ROCK_COLUMNS.GROUP_THRESHOLD) / ROCK_COLUMNS.GROUP_RAMP);
  }

  /**
   * @param {{originX: number, originZ: number, size: number}} region - Tile bounds.
   * @param {object} context
   * @param {(x: number, z: number) => number} context.sampleWeight - This biome's weight.
   * @param {(x: number, z: number) => {elevation: number, slope: number}} context.sampleGroundDetail - Height and
   *   steepness of the *drawn* surface, from one set of samples.
   * @param {(x: number, z: number, radius: number) => boolean} [context.isReserved]
   * @returns {Array<object>} Column descriptions.
   */
  return function collectStructures(region, { sampleWeight, sampleGroundDetail, isReserved }) {
    const spacing = ROCK_COLUMNS.SPACING;
    const xRange = slotRange(region.originX, region.size, spacing);
    const zRange = slotRange(region.originZ, region.size, spacing);

    const columns = [];

    for (let slotX = xRange.first; slotX <= xRange.last; slotX += 1) {
      for (let slotZ = zRange.first; slotZ <= zRange.last; slotZ += 1) {
        const centerX = (slotX + 0.5) * spacing;
        const centerZ = (slotZ + 0.5) * spacing;

        const weight = sampleWeight(centerX, centerZ);
        if (weight <= 0) continue;

        const group = groupStrength(centerX, centerZ);
        if (group <= 0) continue;

        /*
          One stream per slot, keyed by slot index rather than by tile, so the layout does not depend on which tile is
          being built or the order tiles are visited in.

          Roll order is part of the world's identity. Reordering these, or inserting one before an existing one,
          changes every rock field at every seed. Append only.
        */
        const random = createLocalRng(columnSeed, slotX, slotZ);

        const presenceRoll = random();
        const heightRoll = random();
        const profileRoll = random();
        const jitterRollX = random();
        const jitterRollZ = random();
        const spinRoll = random();

        // Density scales with both the group and the biome, so fields fade in rather than stopping at a line.
        if (presenceRoll >= ROCK_COLUMNS.DENSITY * group * weight) continue;

        const shaped = Math.pow(heightRoll, ROCK_COLUMNS.HEIGHT_SKEW);
        const height = lerp(ROCK_COLUMNS.MIN_HEIGHT, ROCK_COLUMNS.MAX_HEIGHT, shaped);
        const profile = profileRoll < ROCK_COLUMNS.HOODOO_CHANCE ? 'hoodoo' : 'spire';

        /*
          Radius is capped against the profile's *widest* ring, not its base.

          A hoodoo's caprock is wider than its footing — that overhang is the entire shape — so reserving only the base
          would let two neighbouring caps come closer than intended and weaken the no-touch guarantee. Dividing by the
          widest ring means `outerRadius` is what actually gets reserved.
        */
        const widest = widestRadiusFor(profile);
        const baseRadius = Math.min(height * ROCK_COLUMNS.RADIUS_RATIO, maxRadius / widest);
        const outerRadius = baseRadius * widest;

        /*
          Jitter limited to the slack left after reserving the widest possible column on each side, plus a gap. Two
          neighbours therefore can never touch — a structural guarantee rather than a collision check, the same one the
          city gets from street width.
        */
        const slack = Math.max(0, (spacing - 2 * maxRadius - ROCK_COLUMNS.CLEARANCE) / 2);
        const x = centerX + (jitterRollX * 2 - 1) * slack;
        const z = centerZ + (jitterRollZ * 2 - 1) * slack;

        /*
          Height and slope from one sample, as the forest does. Probing separately would triple the terrain cost of
          every candidate.
        */
        const { elevation: groundY, slope } = sampleGroundDetail(x, z);

        // A column needs a level footing. Rejected after every roll is consumed, so no other slot shifts.
        if (slope > ROCK_COLUMNS.MAX_GROUND_SLOPE) continue;

        // Keep clear of another biome's structures, so a spire never grows through a tower.
        if (isReserved?.(x, z, outerRadius + ROCK_COLUMNS.CLEARANCE)) continue;

        columns.push({
          kind: 'rockColumn',
          x,
          z,
          groundY,
          height,
          baseRadius,
          /** Widest ring, which is what the slot actually reserves. A hoodoo's cap is wider than its footing. */
          outerRadius,
          sides: ROCK_COLUMNS.SIDES,
          profile,
          spin: spinRoll,
          weight,
          group,
        });
      }
    }

    return columns;
  };
}
